import { AuthRoles } from '@hefe/auth';
import { t } from '@hefe/i18n';
import {
  AssignableType,
  BasketballClubScheduleGame,
  BasketballEventStatus,
  CustomTableColumn,
} from '@hefe/models';
import {
  BasketballGamesSlice,
  ClubSlice,
  GymsSlice,
  ModalsSlice,
  TasksActions,
  currentUserSelector,
} from '@hefe/store';
import { formatDateTime, fullUserName, userHasRole } from '@hefe/utils';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import CustomTable from '../../../core/components/table/custom-table.component';
import AddEditGameForm from '../../../team/forms/add-game/add-edit-game.form';

export default function SchedulePage() {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const games = useSelector(
    BasketballGamesSlice.basketballClubScheduleSelector
  );
  const tasks = useSelector(ClubSlice.clubTasksSelector);
  const user = useSelector(currentUserSelector);

  useEffect(() => {
    fetchGames();
    fetchTasks();
    fetchGyms();
  }, []);

  const renderReferee = (game: BasketballClubScheduleGame, referee: number) => {
    const refereeName = referee === 1 ? game.referee_1 : game.referee_2;
    if (game.own_referees) {
      const ownReferee =
        referee === 1 ? game.own_referee_1 : game.own_referee_2;
      return fullUserName(ownReferee);
    } else {
      return refereeName ?? t('pending');
    }
  };

  const taskColumns = useMemo(() => {
    const columns: CustomTableColumn[] = [];

    tasks
      .filter((task) => userHasRole(AuthRoles.bbadmin, user) || task.is_public)
      .forEach((task) => {
        // get for all games all user assignments of this task and remove duplicates
        const userFilters = games
          .map((game) => {
            const assignmentsOfGame =
              (game.task_assignments ?? {})[task.id] ?? {};
            const assignments = assignmentsOfGame[AssignableType.USER] ?? [];

            return assignments.map((assignment) => assignment.select_option);
          })
          .flat()
          .filter((assignment) => assignment !== undefined)
          .filter(
            (assignment, index, self) =>
              self.findIndex((a) => a?.value === assignment?.value) === index
          )
          .map((assignment) => ({
            text: assignment?.label ?? '',
            value: assignment?.value as any,
          }));

        const teamsFilters = games
          .map((game) => {
            const assignmentsOfGame =
              (game.task_assignments ?? {})[task.id] ?? {};
            const assignments =
              assignmentsOfGame[AssignableType.BASKETBALL_TEAM] ?? [];

            return assignments.map((assignment) => assignment.select_option);
          })
          .flat()
          .filter((assignment) => assignment !== undefined)
          .filter(
            (assignment, index, self) =>
              self.findIndex((a) => a?.value === assignment?.value) === index
          )
          .map((assignment) => ({
            text: assignment?.label ?? '',
            value: assignment?.value as any,
          }));

        // Team Column
        columns.push({
          key: 'task_' + task.id + '_teams',
          title: task.name + ` (${t('teams')})`,
          default: false,
          filters: [
            { text: t('incomplete'), value: 'incomplete' },
            ...teamsFilters,
          ],
          onFilter: (value: any, game: BasketballClubScheduleGame) => {
            const assignmentsOfGame =
              (game.task_assignments ?? {})[task.id] ?? {};
            const assignments =
              assignmentsOfGame[AssignableType.BASKETBALL_TEAM] ?? [];

            if (value === 'incomplete') {
              return assignments.length === 0;
            } else {
              return assignments.some(
                (assignment) => assignment.select_option?.value === value
              );
            }
          },
          render: (_: any, item: BasketballClubScheduleGame) => {
            const assignmentsOfGame =
              (item.task_assignments ?? {})[task.id] ?? {};
            const assignments =
              assignmentsOfGame[AssignableType.BASKETBALL_TEAM] ?? [];

            return assignments
              .map(
                (assignment) => assignment.select_option?.label ?? t('unknown')
              )
              .join(', ');
          },
        });
        // User Column
        columns.push({
          key: 'task_' + task.id + '_users',
          title: task.name + ` (${t('users.users')})`,
          default: false,
          filters: [
            { text: t('incomplete'), value: 'incomplete' },
            ...userFilters,
          ],
          onFilter: (value: any, game: BasketballClubScheduleGame) => {
            const assignmentsOfGame =
              (game.task_assignments ?? {})[task.id] ?? {};
            const assignments = assignmentsOfGame[AssignableType.USER] ?? [];

            if (value === 'incomplete') {
              const minNumberOfUsers = task.min_num_of_users ?? 0;
              return assignments.length < minNumberOfUsers;
            } else {
              return assignments.some(
                (assignment) => assignment.select_option?.value === value
              );
            }
          },
          render: (_: any, item: BasketballClubScheduleGame) => {
            const assignmentsOfGame =
              (item.task_assignments ?? {})[task.id] ?? {};
            const assignments = assignmentsOfGame[AssignableType.USER] ?? [];

            return assignments
              .map(
                (assignment) => assignment.select_option?.label ?? t('unknown')
              )
              .join(', ');
          },
        });
      });

    return columns;
  }, [games, tasks, user]);

  const refereesFilters = useMemo(() => {
    const refereeNames = [];

    for (const game of games) {
      refereeNames.push(renderReferee(game, 1));
      refereeNames.push(renderReferee(game, 2));
    }

    const referees = refereeNames
      .filter((referee, index, self) => self.indexOf(referee) === index)
      .map((referee) => ({ text: referee, value: referee }));

    return [
      { text: t('incomplete'), value: 'incomplete' },
      ...referees,
    ] as CustomTableColumn['filters'];
  }, [games]);

  const fetchGames = () => {
    setLoading(true);
    dispatch(BasketballGamesSlice.fetchBasketballClubGames())
      .unwrap()
      .finally(() => setLoading(false));
  };

  const fetchTasks = () => {
    dispatch(TasksActions.fetchClubTasks());
  };

  const fetchGyms = () => {
    if (!userHasRole(AuthRoles.bbadmin, user)) {
      return;
    }

    dispatch(GymsSlice.fetchGyms({ cacheDuration: 5 * 60 * 1000 }));
  };

  const openGame = (game: BasketballClubScheduleGame) => {
    dispatch(
      ModalsSlice.modalsActions.openGameModal({
        id: game.id,
      })
    );
  };

  return (
    <CustomTable
      name="schedule"
      rowKey="id"
      loading={loading}
      dataSource={games}
      onRowClick={(item) => openGame(item)}
      strikeThroughRowKeys={games
        .filter((g) => g.status === BasketballEventStatus.CANCELED)
        .map((g) => g.id)}
      addForm={
        userHasRole(AuthRoles.bbadmin, user)
          ? {
              component: <AddEditGameForm />,
              title: t('games.add'),
              formId: `add-edit-game-form-new`,
            }
          : undefined
      }
      columns={[
        {
          key: 'date',
          dataIndex: 'date',
          title: t('date'),
          showDateFilter: true,
          default: true,
          render: (value) => formatDateTime(value),
        },
        {
          key: 'home',
          dataIndex: 'home',
          title: t('home'),
          showFilter: true,
          default: true,
        },
        {
          key: 'guest',
          dataIndex: 'guest',
          title: t('guest'),
          showFilter: true,
          default: true,
        },
        {
          key: 'gym',
          dataIndex: 'gym',
          title: t('gym'),
          showFilter: true,
          default: true,
        },
        {
          key: 'game_number',
          dataIndex: 'game_number',
          title: t('games.number'),
          default: false,
        },
        {
          key: 'league',
          dataIndex: 'league',
          showFilter: true,
          title: t('leagues.singular'),
          default: false,
        },
        {
          key: 'is_home',
          dataIndex: 'is_home',
          showFilter: true,
          title: t('home_game'),
          default: false,
          render: (value: boolean) => (value ? t('home_game') : t('away_game')),
        },
        {
          key: 'referees',
          dataIndex: 'referees',
          title: t('referees.plural'),
          default: false,
          filters: refereesFilters,
          onFilter: (value: any, game: BasketballClubScheduleGame) => {
            const referee1 = renderReferee(game, 1);
            const referee2 = renderReferee(game, 2);

            if (value === 'incomplete') {
              return (
                game.own_referees &&
                (referee1 === t('pending') || referee2 === t('pending'))
              );
            } else {
              return referee1 === value || referee2 === value;
            }
          },
          render: (referee, item) =>
            `${renderReferee(item, 1)}, ${renderReferee(item, 2)}`,
        },
        ...taskColumns,
      ]}
    />
  );
}
